import React, { useState, useMemo, useCallback, useEffect } from 'react'
import { MoreHorizontal, ChevronLeft, ChevronRight } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import type { AdminWorkspace } from '../types/index.js'
import WorkspaceDetailPanel from './WorkspaceDetailPanel.js'
import WorkspaceActivityDrawer from './WorkspaceActivityDrawer.js'

interface Props {
  workspaces: AdminWorkspace[]
  total: number
  page: number
  limit: number
  loading: boolean
  onPageChange: (p: number) => void
  onUpdateWorkspace: (id: string, payload: Partial<{ isActive: boolean }>) => Promise<void>
  onAssignPlan: (id: string, planId: string | null) => Promise<void>
  initialWorkspaceId?: string
}

function formatDate(iso: string) {
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  } catch {
    return iso
  }
}

const SkeletonRow: React.FC<{ border: string }> = ({ border }) => (
  <tr>
    {[60, 40, 30, 50, 30].map((w, i) => (
      <td key={i} className="px-4 py-3">
        <div
          className="h-4 rounded-full animate-pulse"
          style={{ backgroundColor: border, width: `${w}%` }}
        />
      </td>
    ))}
  </tr>
)

const WorkspaceTable: React.FC<Props> = ({
  workspaces,
  total,
  page,
  limit,
  loading,
  onPageChange,
  onUpdateWorkspace,
  onAssignPlan,
  initialWorkspaceId,
}) => {
  const { currentTheme } = useTheme()
  const [search, setSearch] = useState('')
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [openDropdown, setOpenDropdown] = useState<string | null>(null)
  const [selectedWorkspace, setSelectedWorkspace] = useState<AdminWorkspace | null>(null)
  const [logsWorkspace, setLogsWorkspace] = useState<AdminWorkspace | null>(null)

  // Auto-open the detail panel when navigated here from another page with a workspace ID
  useEffect(() => {
    if (!initialWorkspaceId || workspaces.length === 0) return
    const match = workspaces.find((w) => w.id === initialWorkspaceId)
    if (match) setSelectedWorkspace(match)
  }, [initialWorkspaceId, workspaces])

  const filtered = useMemo(() => {
    if (!search.trim()) return workspaces
    const q = search.toLowerCase()
    return workspaces.filter(
      (w) =>
        w.name.toLowerCase().includes(q) ||
        (w.description ?? '').toLowerCase().includes(q),
    )
  }, [workspaces, search])

  const totalPages = Math.max(1, Math.ceil(total / limit))

  const handleAction = useCallback(
    async (id: string, payload: Partial<{ isActive: boolean }>) => {
      setActionLoading(id)
      setOpenDropdown(null)
      try {
        await onUpdateWorkspace(id, payload)
      } finally {
        setActionLoading(null)
      }
    },
    [onUpdateWorkspace],
  )

  const handleRowClick = useCallback((ws: AdminWorkspace) => {
    setSelectedWorkspace(ws)
  }, [])

  const handleViewLogs = useCallback((ws: AdminWorkspace) => {
    setLogsWorkspace(ws)
    setOpenDropdown(null)
  }, [])

  // Keep panel in sync when workspace data updates (e.g. after suspend/activate)
  const selectedId = selectedWorkspace?.id
  const syncedSelected = useMemo(
    () => (selectedId ? (workspaces.find((w) => w.id === selectedId) ?? selectedWorkspace) : null),
    [selectedId, workspaces, selectedWorkspace],
  )

  return (
    <div>
      {/* Search */}
      <div className="mb-4">
        <input
          type="text"
          placeholder="Search by name or description..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full sm:w-80 rounded-2xl border px-4 py-2.5 text-sm outline-none transition"
          style={{
            backgroundColor: currentTheme.inputBackground,
            color: currentTheme.text,
            borderColor: currentTheme.border,
          }}
        />
      </div>

      {/* Table */}
      <div
        className="rounded-2xl border overflow-hidden"
        style={{ borderColor: currentTheme.border }}
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ backgroundColor: currentTheme.secondary }}>
                {['Name', 'Members', 'Status', 'Created', 'Actions'].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide"
                    style={{ color: currentTheme.secondrytext }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading
                ? Array.from({ length: 5 }).map((_, i) => (
                    <SkeletonRow key={i} border={currentTheme.border} />
                  ))
                : filtered.length === 0
                ? (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-4 py-12 text-center text-sm"
                        style={{ color: currentTheme.secondrytext }}
                      >
                        No workspaces found.
                      </td>
                    </tr>
                  )
                : filtered.map((ws) => (
                    <tr
                      key={ws.id}
                      className="border-t transition-colors cursor-pointer"
                      style={{ borderColor: currentTheme.border }}
                      onClick={() => handleRowClick(ws)}
                      onMouseEnter={(e) => {
                        (e.currentTarget as HTMLTableRowElement).style.backgroundColor =
                          currentTheme.hoverBackground
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLTableRowElement).style.backgroundColor = 'transparent'
                      }}
                    >
                      <td className="px-4 py-3 font-medium" style={{ color: currentTheme.text }}>
                        {ws.name}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold"
                          style={{
                            backgroundColor: `${currentTheme.accent}22`,
                            color: currentTheme.accent,
                          }}
                        >
                          {ws.memberCount ?? 0} members
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold"
                          style={
                            ws.isActive
                              ? { backgroundColor: currentTheme.successBackground, color: currentTheme.successText }
                              : { backgroundColor: currentTheme.errorBackground, color: currentTheme.errorText }
                          }
                        >
                          {ws.isActive ? 'Active' : 'Suspended'}
                        </span>
                      </td>
                      <td className="px-4 py-3" style={{ color: currentTheme.secondrytext }}>
                        {formatDate(ws.createdAt)}
                      </td>
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <div className="relative">
                          <button
                            type="button"
                            disabled={actionLoading === ws.id}
                            onClick={() =>
                              setOpenDropdown((prev) => (prev === ws.id ? null : ws.id))
                            }
                            className="inline-flex items-center justify-center w-8 h-8 rounded-lg transition"
                            style={{
                              color: currentTheme.secondrytext,
                              backgroundColor: 'transparent',
                            }}
                            onMouseEnter={(e) => {
                              (e.currentTarget as HTMLButtonElement).style.backgroundColor =
                                currentTheme.hoverBackground
                            }}
                            onMouseLeave={(e) => {
                              (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent'
                            }}
                            aria-label="Actions"
                          >
                            <MoreHorizontal size={16} />
                          </button>

                          {openDropdown === ws.id && (
                            <>
                              <div
                                className="fixed inset-0 z-10"
                                onClick={() => setOpenDropdown(null)}
                              />
                              <div
                                className="absolute right-0 top-9 z-20 min-w-[140px] rounded-xl border shadow-lg py-1"
                                style={{
                                  backgroundColor: currentTheme.secondary,
                                  borderColor: currentTheme.border,
                                  boxShadow: currentTheme.boxShadow,
                                }}
                              >
                                <button
                                  type="button"
                                  onClick={() => handleViewLogs(ws)}
                                  className="w-full text-left px-4 py-2 text-sm transition"
                                  style={{ color: currentTheme.text }}
                                  onMouseEnter={(e) => {
                                    (e.currentTarget as HTMLButtonElement).style.backgroundColor =
                                      currentTheme.hoverBackground
                                  }}
                                  onMouseLeave={(e) => {
                                    (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent'
                                  }}
                                >
                                  View Logs
                                </button>
                                <div style={{ height: '1px', backgroundColor: currentTheme.border, margin: '2px 0' }} />
                                <button
                                  type="button"
                                  onClick={() => handleAction(ws.id, { isActive: !ws.isActive })}
                                  className="w-full text-left px-4 py-2 text-sm transition"
                                  style={{
                                    color: ws.isActive ? currentTheme.errorText : currentTheme.successText,
                                  }}
                                  onMouseEnter={(e) => {
                                    (e.currentTarget as HTMLButtonElement).style.backgroundColor =
                                      currentTheme.hoverBackground
                                  }}
                                  onMouseLeave={(e) => {
                                    (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent'
                                  }}
                                >
                                  {ws.isActive ? 'Suspend' : 'Activate'}
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="mt-4 flex items-center justify-between">
        <p className="text-sm" style={{ color: currentTheme.secondrytext }}>
          Page {page} of {totalPages} &mdash; {total} total
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={page <= 1 || loading}
            onClick={() => onPageChange(page - 1)}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border text-sm font-medium transition disabled:opacity-40 disabled:cursor-not-allowed"
            style={{
              backgroundColor: currentTheme.secondary,
              color: currentTheme.text,
              borderColor: currentTheme.border,
            }}
          >
            <ChevronLeft size={15} />
            Previous
          </button>
          <button
            type="button"
            disabled={page >= totalPages || loading}
            onClick={() => onPageChange(page + 1)}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border text-sm font-medium transition disabled:opacity-40 disabled:cursor-not-allowed"
            style={{
              backgroundColor: currentTheme.secondary,
              color: currentTheme.text,
              borderColor: currentTheme.border,
            }}
          >
            Next
            <ChevronRight size={15} />
          </button>
        </div>
      </div>

      {/* Detail panel */}
      <WorkspaceDetailPanel
        workspace={syncedSelected}
        onClose={() => setSelectedWorkspace(null)}
        onUpdateWorkspace={onUpdateWorkspace}
        onAssignPlan={onAssignPlan}
      />

      {/* Activity log drawer */}
      <WorkspaceActivityDrawer
        workspaceId={logsWorkspace?.id ?? null}
        {...(logsWorkspace?.name ? { workspaceName: logsWorkspace.name } : {})}
        onClose={() => setLogsWorkspace(null)}
      />
    </div>
  )
}

export default React.memo(WorkspaceTable)
